import { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  ArrowLeft,
  Paperclip,
  Send,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Link2,
  Undo2,
  Redo2,
  Eraser,
  Minus,
  Trash2,
  Mail,
  Maximize2,
  Minimize2,
  ChevronDown,
  Quote,
  AlignLeft,
  AlignCenter,
  AlignRight,
  FileText,
  Check,
  Save,
} from "lucide-react";
import { useApi } from "../../hooks/useApi";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useAuthStore } from "../../stores/authStore";
import { sanitizeHtml } from "../../utils/sanitize";
import { confirmDialog, alertDialog } from "../../stores/dialogStore";
import { addToOutbox, generateClientMessageId } from "../../utils/offlineStorage";

interface ComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSent?: () => void;
  replyTo?: {
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    body: string;
    bodyHtml?: string;
    mode?: "reply" | "forward" | "draft";
    draftId?: string;
    draftFolder?: string;
    attachments?: {
      filename: string;
      contentType: string;
      size: number;
      contentBase64?: string;
      contentId?: string;
    }[];
  };
}

function base64ToFile(base64: string, filename: string, mimeType: string): File {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType || "application/octet-stream" });
}

function parseRecipients(input: string): string[] {
  return input
    .split(/[,;\s]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s));
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
}

const TOOLBAR_GROUPS = [
  [
    { cmd: "bold", icon: Bold, title: "Negrita (Ctrl+B)" },
    { cmd: "italic", icon: Italic, title: "Cursiva (Ctrl+I)" },
    { cmd: "underline", icon: Underline, title: "Subrayado (Ctrl+U)" },
    { cmd: "strikeThrough", icon: Strikethrough, title: "Tachado" },
  ],
  [
    { cmd: "insertUnorderedList", icon: List, title: "Lista con viñetas" },
    { cmd: "insertOrderedList", icon: ListOrdered, title: "Lista numerada" },
    { cmd: "formatBlock", val: "blockquote", icon: Quote, title: "Cita" },
  ],
  [
    { cmd: "justifyLeft", icon: AlignLeft, title: "Alinear a la izquierda" },
    { cmd: "justifyCenter", icon: AlignCenter, title: "Centrar" },
    { cmd: "justifyRight", icon: AlignRight, title: "Alinear a la derecha" },
  ],
  [
    { cmd: "insertHorizontalRule", icon: Minus, title: "Línea horizontal" },
    { cmd: "removeFormat", icon: Eraser, title: "Limpiar formato" },
    { cmd: "undo", icon: Undo2, title: "Deshacer (Ctrl+Z)" },
    { cmd: "redo", icon: Redo2, title: "Rehacer (Ctrl+Y)" },
  ],
];

export default function ComposeModal({
  isOpen,
  onClose,
  onSent,
  replyTo,
}: ComposeModalProps) {
  const { post, get } = useApi();
  const user = useAuthStore((s) => s.user);
  const backdropRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const toInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [sender, setSender] = useState(user?.email || "");
  const [availableAliases, setAvailableAliases] = useState<string[]>([]);
  const [to, setTo] = useState(replyTo?.to || "");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [subject, setSubject] = useState(replyTo?.subject || "");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftSavedMessage, setDraftSavedMessage] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [sendError, setSendError] = useState<string | null>(null);
  const [toError, setToError] = useState<string | null>(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [rawSignature, setRawSignature] = useState("");
  const [includeSignature, setIncludeSignature] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // En móvil (< sm, 640px) el composer siempre se muestra a pantalla completa
  // para aprovechar todo el espacio disponible: forzamos fullscreen + desminimizado.
  const isMobile = useMediaQuery("(max-width: 639px)");
  useEffect(() => {
    if (isMobile && isOpen) {
      setIsMinimized(false);
      setIsFullscreen(true);
    }
  }, [isMobile, isOpen]);

  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const [isDragging, setIsDragging] = useState(false);

  const initializedRef = useRef(false);
  const userTouchedRef = useRef(false);

  const execCmd = useCallback((cmd: string, val?: string) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    checkEditorEmpty();
  }, []);

  const checkEditorEmpty = useCallback(() => {
    if (!editorRef.current) return;
    const text = editorRef.current.innerText || "";
    const html = editorRef.current.innerHTML || "";
    const empty = text.trim() === "" && (html === "" || html === "<br>" || html === "<div><br></div>");
    setIsEditorEmpty(empty);
  }, []);

  const [draftId, setDraftId] = useState<string | undefined>(replyTo?.draftId);
  const [draftFolder, setDraftFolder] = useState<string | undefined>(replyTo?.draftFolder);

  const getModalTitle = () => {
    if (replyTo?.mode === "draft") return "Editar borrador";
    if (replyTo?.mode === "forward") return "Reenviar mensaje";
    if (replyTo?.mode === "reply") return "Responder mensaje";
    if (replyTo) {
      return replyTo.subject?.toLowerCase().startsWith("fwd")
        ? "Reenviar mensaje"
        : "Responder mensaje";
    }
    return "Nuevo mensaje";
  };

  const handleInsertLink = useCallback(() => {
    setLinkUrl("");
    setShowLinkDialog(true);
  }, []);

  const confirmInsertLink = () => {
    if (linkUrl && isValidEmail(linkUrl) === false && !linkUrl.startsWith("http")) {
      const fullUrl = linkUrl.startsWith("www.") ? `https://${linkUrl}` : linkUrl;
      execCmd("createLink", fullUrl);
    } else if (linkUrl) {
      execCmd("createLink", linkUrl);
    }
    setShowLinkDialog(false);
    setLinkUrl("");
  };

  const toggleSignature = () => {
    if (!editorRef.current || !rawSignature) return;

    if (includeSignature) {
      const existingSig = editorRef.current.querySelector("#email-signature-block");
      if (existingSig) {
        existingSig.remove();
      }
      setIncludeSignature(false);
    } else {
      const existingSig = editorRef.current.querySelector("#email-signature-block");
      if (!existingSig) {
        const isHtml = /<[a-z][\s\S]*>/i.test(rawSignature);
        const safeSig = isHtml
          ? sanitizeHtml(rawSignature).html
          : escapeHtml(rawSignature).replace(/(?:\r\n|\r|\n)/g, "<br>");
        const sigBlock = document.createElement("div");
        sigBlock.id = "email-signature-block";
        sigBlock.className = "email-signature-block";
        sigBlock.innerHTML =
          '<hr class="email-signature-sep">' +
          safeSig;
        editorRef.current.appendChild(sigBlock);
      }
      setIncludeSignature(true);
    }
    checkEditorEmpty();
  };

  const hasUserWrittenContent = () => {
    // Si hay destinatarios o asunto escritos
    if (to.trim() || cc.trim() || bcc.trim() || subject.trim() || attachments.length > 0) {
      return true;
    }

    if (!editorRef.current) return false;

    // Clonamos el editor para evaluar si hay texto real del usuario fuera del bloque de firma o quotes
    const clone = editorRef.current.cloneNode(true) as HTMLElement;
    const sigBlock = clone.querySelector("#email-signature-block");
    if (sigBlock) sigBlock.remove();

    const userText = (clone.innerText || "").replace(/[\r\n\t\s]+/g, " ").trim();
    return userText.length > 0;
  };

  const handleSaveDraft = async (silent = false) => {
    if (!hasUserWrittenContent()) return;

    const text = editorRef.current?.innerText || "";
    const html = editorRef.current?.innerHTML || "";

    if (!silent) setIsSavingDraft(true);

    try {
      localStorage.setItem(
        "mf_draft_backup",
        JSON.stringify({
          to,
          cc,
          bcc,
          subject,
          html,
          text,
          updatedAt: Date.now(),
        })
      );

      const toRecipients = to ? parseRecipients(to) : [];
      const ccRecipients = cc ? parseRecipients(cc) : [];
      const bccRecipients = bcc ? parseRecipients(bcc) : [];

      const res = await post<{ success: boolean; uid?: number }>("/mail/drafts", {
        to: toRecipients.length > 0 ? toRecipients : undefined,
        cc: ccRecipients.length > 0 ? ccRecipients : undefined,
        bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
        subject: subject || "(Borrador)",
        text: text || undefined,
        html: html || undefined,
        draftId: draftId ? Number(draftId) : undefined,
        draftFolder: draftFolder || "Drafts",
      });

      if (res?.uid) {
        setDraftId(String(res.uid));
      }

      setDraftSavedMessage("Borrador guardado ✓");
      setTimeout(() => setDraftSavedMessage(null), 2500);
    } catch (err) {
      console.warn("Could not sync draft to server:", err);
      setDraftSavedMessage("Borrador guardado localmente");
      setTimeout(() => setDraftSavedMessage(null), 2000);
    } finally {
      if (!silent) setIsSavingDraft(false);
    }
  };

  const handleDiscard = async () => {
    if (hasUserWrittenContent()) {
      const ok = await confirmDialog({
        title: "¿Descartar mensaje?",
        message: "¿Estás seguro de que deseas descartar este mensaje? Todos los cambios no guardados se perderán permanentemente.",
        confirmText: "Descartar",
        cancelText: "Continuar escribiendo",
        variant: "danger",
      });
      if (!ok) return;
    }

    // Si existía un borrador en el servidor para este modal, borrarlo
    if (draftId && draftFolder) {
      try {
        await post("/mail/drafts/delete", {
          draftId: Number(draftId),
          folder: draftFolder,
        });
      } catch {
        // silent
      }
    }

    localStorage.removeItem("mf_draft_backup");
    onClose();
  };

  const handleClose = () => {
    if (hasUserWrittenContent()) {
      handleSaveDraft(true);
    }
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      userTouchedRef.current = false;
      setShowLinkDialog(false);
      setLinkUrl("");
      setToError(null);
      setIsEditorEmpty(true);
      setDraftSavedMessage(null);
      return;
    }
    setTo(replyTo?.to || "");
    setCc(replyTo?.cc || "");
    setBcc(replyTo?.bcc || "");
    setSubject(replyTo?.subject || "");
    setShowCcBcc(!!(replyTo?.cc || replyTo?.bcc));
    setIsSending(false);
    setIsSavingDraft(false);
    setSendError(null);
    if (replyTo?.attachments && replyTo.attachments.length > 0) {
      const initialFiles: File[] = [];
      for (const att of replyTo.attachments) {
        if (att.contentBase64) {
          try {
            initialFiles.push(base64ToFile(att.contentBase64, att.filename, att.contentType));
          } catch (e) {
            console.error("Error converting attachment to file:", e);
          }
        }
      }
      setAttachments(initialFiles);
    } else {
      setAttachments([]);
    }
    setIncludeSignature(replyTo?.mode !== "draft");
    setDraftId(replyTo?.draftId);
    setDraftFolder(replyTo?.draftFolder);
    setIsMinimized(false);

    get<{ signature?: string; aliases?: string[] }>("/mail/settings")
      .then((data) => {
        const sig = data.signature || "";
        const aliases = data.aliases || [];
        setRawSignature(sig);
        setAvailableAliases(aliases);
        setSender(user?.email || "");
        return sig;
      })
      .catch(() => "")
      .then((sig) => {
        setTimeout(() => {
          if (!editorRef.current) return;
          if (initializedRef.current) return;
          if (userTouchedRef.current) return;
          initializedRef.current = true;

          if (replyTo?.mode === "draft") {
            // Cargar directamente el contenido del borrador para edición directa
            if (replyTo.bodyHtml) {
              const sanitized = sanitizeHtml(replyTo.bodyHtml);
              editorRef.current.innerHTML = sanitized.html;
            } else if (replyTo.body) {
              editorRef.current.innerHTML = escapeHtml(replyTo.body).replace(/(?:\r\n|\r|\n)/g, "<br>");
            } else {
              editorRef.current.innerHTML = "";
            }
          } else if (replyTo?.bodyHtml) {
            const sanitized = sanitizeHtml(replyTo.bodyHtml);
            editorRef.current.innerHTML =
              '<br><br><div class="email-quote-block">' +
              sanitized.html +
              "</div>";
          } else if (replyTo?.body) {
            editorRef.current.innerHTML =
              '<br><br><div class="email-quote-block">' +
              escapeHtml(replyTo.body) +
              "</div>";
          } else {
            editorRef.current.innerHTML = "";
          }

          if (sig && replyTo?.mode !== "draft") {
            const isHtml = /<[a-z][\s\S]*>/i.test(sig);
            const safeSig = isHtml
              ? sanitizeHtml(sig).html
              : escapeHtml(sig).replace(/(?:\r\n|\r|\n)/g, "<br>");
            const sigBlock = document.createElement("div");
            sigBlock.id = "email-signature-block";
            sigBlock.className = "email-signature-block";
            sigBlock.innerHTML =
              '<hr class="email-signature-sep">' +
              safeSig;

            // Asegurar que siempre exista un párrafo editable antes de la firma
            if (!editorRef.current.innerHTML || editorRef.current.innerHTML.trim() === "") {
              const initialLine = document.createElement("p");
              initialLine.innerHTML = "<br>";
              editorRef.current.appendChild(initialLine);
            }
            editorRef.current.appendChild(sigBlock);
          } else if (!editorRef.current.innerHTML || editorRef.current.innerHTML.trim() === "") {
            const initialLine = document.createElement("p");
            initialLine.innerHTML = "<br>";
            editorRef.current.appendChild(initialLine);
          }

          checkEditorEmpty();

          // Si es respuesta o edición enfocar cursor al inicio
          if (replyTo) {
            const range = document.createRange();
            const sel = window.getSelection();
            if (editorRef.current.firstChild) {
              range.setStart(editorRef.current.firstChild, 0);
              range.collapse(true);
              sel?.removeAllRanges();
              sel?.addRange(range);
            }
          }
        }, 50);
      });
  }, [isOpen, replyTo, get, checkEditorEmpty]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !showLinkDialog) {
        handleClose();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        if (!isSending && to.trim()) {
          handleSend();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSaveDraft();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, isSending, to, showLinkDialog, subject]);

  useEffect(() => {
    if (!to) {
      setToError(null);
      return;
    }
    const recipients = parseRecipients(to);
    const invalid = to.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.length > 0);
    const invalidEmails = invalid.filter((s) => !isValidEmail(s));
    if (invalidEmails.length > 0) {
      setToError(`Correo inválido: ${invalidEmails[0]}`);
    } else if (recipients.length === 0) {
      setToError("Ingresa al menos un destinatario válido");
    } else {
      setToError(null);
    }
  }, [to]);

  const handleSend = async () => {
    const recipients = parseRecipients(to);
    if (recipients.length === 0) {
      setToError("Ingresa al menos un destinatario válido");
      toInputRef.current?.focus();
      return;
    }
    if (!subject.trim()) {
      const ok = await confirmDialog({
        title: "¿Enviar sin asunto?",
        message: "El correo no tiene asunto. ¿Deseas enviarlo de todas formas?",
        confirmText: "Enviar de todas formas",
        cancelText: "Volver a redactar",
        variant: "warning",
      });
      if (!ok) return;
    }

    setIsSending(true);
    setSendError(null);
    try {
      // Clave de idempotencia: si la petición se reintenta (red caída) o se envía
      // más tarde desde la cola, el servidor no volverá a entregar el correo.
      const clientMessageId = generateClientMessageId();
      const html = editorRef.current?.innerHTML || "";
      const text = editorRef.current?.innerText || "";

      let attachmentsPayload: { filename: string; contentBase64: string; contentType: string }[] | undefined;
      if (attachments.length > 0) {
        attachmentsPayload = await Promise.all(
          attachments.map(async (file) => {
            const arrayBuffer = await file.arrayBuffer();
            const base64 = btoa(
              new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), "")
            );
            return {
              filename: file.name,
              contentBase64: base64,
              contentType: file.type || "application/octet-stream",
            };
          })
        );
      }

      const ccRecipients = cc ? parseRecipients(cc) : [];
      const bccRecipients = bcc ? parseRecipients(bcc) : [];

      const payload = {
        from: sender || undefined,
        to: recipients,
        cc: ccRecipients.length > 0 ? ccRecipients : undefined,
        bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
        subject: subject || "(sin asunto)",
        text: text.trim() || undefined,
        html: html.trim() || undefined,
        attachments: attachmentsPayload,
        clientMessageId,
        draftId: draftId ? Number(draftId) : undefined,
        draftFolder: draftFolder || "Drafts",
      };

      if (!navigator.onLine) {
        // Modo offline directo: guardar en cola Outbox
        await addToOutbox({
          from: sender || undefined,
          to: recipients,
          cc: ccRecipients.length > 0 ? ccRecipients : undefined,
          bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
          subject: subject || "(sin asunto)",
          body: text.trim() || undefined,
          bodyHtml: html.trim() || undefined,
          clientMessageId,
          attachments: attachmentsPayload?.map((att) => ({
            name: att.filename,
            type: att.contentType,
            size: 0,
            base64: att.contentBase64,
          })),
        });

        localStorage.removeItem("mf_draft_backup");
        alertDialog(
          "Sin conexión a Internet. El correo se guardó en la cola de salida y se enviará automáticamente al reconectarte.",
          "Guardado en Cola de Salida"
        );
        onClose();
        return;
      }

      try {
        await post("/mail/send", payload);
        localStorage.removeItem("mf_draft_backup");
        if (onSent) onSent();
        onClose();
      } catch (postErr: any) {
        // Si el fallo fue por pérdida repentina de red
        if (!navigator.onLine || postErr.message?.includes("Failed to fetch") || postErr.message?.includes("NetworkError")) {
          await addToOutbox({
            from: sender || undefined,
            to: recipients,
            cc: ccRecipients.length > 0 ? ccRecipients : undefined,
            bcc: bccRecipients.length > 0 ? bccRecipients : undefined,
            subject: subject || "(sin asunto)",
            body: text.trim() || undefined,
            bodyHtml: html.trim() || undefined,
            clientMessageId,
            attachments: attachmentsPayload?.map((att) => ({
              name: att.filename,
              type: att.contentType,
              size: 0,
              base64: att.contentBase64,
            })),
          });

          localStorage.removeItem("mf_draft_backup");
          alertDialog(
            "Se perdió la conexión. El correo se guardó en la cola de salida y se enviará automáticamente al restablecerse la red.",
            "Guardado en Cola de Salida"
          );
          onClose();
          return;
        }
        throw postErr;
      }
    } catch (err: any) {
      const msg = err?.message || err?.error || "Error al enviar correo";
      setSendError(msg);
    } finally {
      setIsSending(false);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const addFiles = (files: File[]) => {
    const totalSize = [...attachments, ...files].reduce((sum, f) => sum + f.size, 0);
    if (totalSize > 25 * 1024 * 1024) {
      alertDialog("El tamaño total de los adjuntos no puede superar 25 MB", "Límite excedido");
      return;
    }
    setAttachments((prev) => [...prev, ...files]);
  };

  const handleAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
      if (e.target) e.target.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  if (!isOpen) return null;

  // Lista consolidada de identidades remitentes (correo principal + aliases)
  const allSenders = Array.from(
    new Set([user?.email || "", ...availableAliases].filter(Boolean))
  );

  return (
    <div
      ref={backdropRef}
      className={`fixed z-50 transition-all duration-200 pointer-events-none ${
        isFullscreen
          ? "inset-0 pointer-events-auto flex items-center justify-center bg-black/60 p-0 sm:p-4"
          : "bottom-0 right-0 sm:right-6 pointer-events-none"
      }`}
      onMouseDown={(e) => {
        if (isFullscreen && e.target === backdropRef.current && !showLinkDialog) {
          handleClose();
        }
      }}
    >
      <div
        className={`pointer-events-auto flex flex-col bg-[var(--bg-primary)] border border-[var(--border-color)] shadow-2xl overflow-hidden transition-all duration-150 ${
          isFullscreen
            ? "w-full h-full"
            : isMinimized
            ? "w-full sm:w-[320px] h-[44px]"
            : "w-full sm:w-[620px] md:w-[680px] lg:w-[740px] h-[100dvh] sm:h-[580px] sm:max-h-[85vh]"
        }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        {/* Header bar (Desktop & Mobile optimized) */}
        <div
          className="flex items-center justify-between px-3.5 sm:px-4 pt-[max(0.625rem,env(safe-area-inset-top))] sm:pt-3 pb-2.5 sm:pb-3 border-b border-[var(--border-color)] bg-[var(--bg-secondary)] flex-shrink-0 cursor-pointer select-none"
          onClick={() => {
            if (!isFullscreen) setIsMinimized(!isMinimized);
          }}
        >
          {/* Mobile Back button / Desktop Title */}
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="sm:hidden p-1.5 -ml-1 hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)]"
              title="Volver (guarda borrador)"
              aria-label="Volver (guarda borrador)"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="hidden sm:flex w-6 h-6 bg-accent/15 text-accent items-center justify-center flex-shrink-0">
              <Mail size={14} />
            </div>
            <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">
              {subject.trim() ? subject : getModalTitle()}
            </h3>

            {/* Signature toggle button (solo cuando no está minimizado) */}
            {!isMinimized && rawSignature && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSignature();
                }}
                className={`hidden md:flex items-center gap-1 text-2xs px-2 py-0.5 border transition-colors ${
                  includeSignature
                    ? "bg-accent/15 border-accent/40 text-accent font-semibold"
                    : "bg-[var(--bg-tertiary)] border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
                title={includeSignature ? "Desactivar firma en este correo" : "Activar firma en este correo"}
              >
                <FileText size={11} />
                <span>{includeSignature ? "Firma activa" : "Sin firma"}</span>
                {includeSignature && <Check size={10} />}
              </button>
            )}

            {/* Draft status feedback badge */}
            {draftSavedMessage && !isMinimized && (
              <span className="text-2xs text-green-500 font-medium px-2 py-0.5 bg-green-500/10 border border-green-500/20 animate-fadeIn">
                {draftSavedMessage}
              </span>
            )}
          </div>

          {/* Top-right quick actions */}
          <div
            className="flex items-center gap-1 flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Desktop window controls */}
            {!isFullscreen && (
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="hidden sm:block p-1.5 hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                title={isMinimized ? "Expandir ventana" : "Minimizar ventana"}
              >
                <Minus size={15} />
              </button>
            )}
            <button
              onClick={() => {
                setIsMinimized(false);
                setIsFullscreen(!isFullscreen);
              }}
              className="hidden sm:block p-1.5 hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
              title={isFullscreen ? "Restaurar a ventana flotante" : "Pantalla completa"}
            >
              {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-red-500/10 text-[var(--text-secondary)] hover:text-red-500 transition-colors"
              title="Cerrar y guardar borrador (Esc)"
              aria-label="Cerrar y guardar borrador"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Si está minimizado, ocultar cuerpo y controles */}
        {!isMinimized && (
          <>
            {/* Recipient inputs */}
            <div className="border-b border-[var(--border-color)] text-xs flex-shrink-0 bg-[var(--bg-primary)]">
              {/* FROM / REMITENTE (Selector de Aliases si hay más de una identidad) */}
              {allSenders.length > 1 ? (
                <div className="flex items-center px-4 sm:px-5 py-2 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]/30">
                  <span className="text-[var(--text-muted)] font-medium text-xs w-12 flex-shrink-0 select-none">
                    De
                  </span>
                  <div className="flex-1 relative flex items-center">
                    <select
                      value={sender}
                      onChange={(e) => setSender(e.target.value)}
                      className="w-full py-0.5 text-sm bg-transparent text-accent font-semibold outline-none cursor-pointer appearance-none pr-6"
                    >
                      {allSenders.map((s) => (
                        <option key={s} value={s} className="bg-[var(--bg-primary)] text-[var(--text-primary)] font-normal">
                          {s} {s === user?.email ? "(Principal)" : "(Alias)"}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-0 text-[var(--text-muted)] pointer-events-none" />
                  </div>
                </div>
              ) : null}

              {/* TO */}
              <div className={`flex items-center px-4 sm:px-5 py-2 border-b border-[var(--border-subtle)] ${toError ? "bg-red-500/5" : ""}`}>
                <span className="text-[var(--text-muted)] font-medium text-xs w-12 flex-shrink-0 select-none">
                  Para
                </span>
                <input
                  ref={toInputRef}
                  type="text"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="flex-1 py-0.5 text-sm bg-transparent text-[var(--text-primary)] outline-none"
                  autoFocus={!replyTo}
                />
                <button
                  type="button"
                  onClick={() => setShowCcBcc(!showCcBcc)}
                  className="px-2 py-0.5 text-2xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors select-none"
                >
                  {showCcBcc ? "Ocultar" : "Cc / Bcc"}
                </button>
              </div>

              {toError && (
                <div className="px-4 sm:px-5 py-1 text-2xs text-red-500 bg-red-500/10 font-medium border-b border-red-500/20">
                  {toError}
                </div>
              )}

              {/* CC & BCC */}
              {showCcBcc && (
                <>
                  <div className="flex items-center px-4 sm:px-5 py-1.5 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]/20 animate-fadeIn">
                    <span className="text-[var(--text-muted)] font-medium text-xs w-12 flex-shrink-0 select-none">
                      Cc
                    </span>
                    <input
                      type="text"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      className="flex-1 py-0.5 text-sm bg-transparent text-[var(--text-primary)] outline-none"
                    />
                  </div>
                  <div className="flex items-center px-4 sm:px-5 py-1.5 border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]/20 animate-fadeIn">
                    <span className="text-[var(--text-muted)] font-medium text-xs w-12 flex-shrink-0 select-none">
                      Bcc
                    </span>
                    <input
                      type="text"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      className="flex-1 py-0.5 text-sm bg-transparent text-[var(--text-primary)] outline-none"
                    />
                  </div>
                </>
              )}

              {/* SUBJECT */}
              <div className="flex items-center px-4 sm:px-5 py-2">
                <span className="text-[var(--text-muted)] font-medium text-xs w-12 flex-shrink-0 select-none">
                  Asunto
                </span>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="flex-1 py-0.5 text-sm bg-transparent text-[var(--text-primary)] outline-none font-medium"
                />
              </div>
            </div>

        {/* Rich Formatting Toolbar with Modern Styled Tooltips */}
        <div className="flex items-center gap-1 px-3 py-1.5 sm:py-2 border-b border-[var(--border-color)] bg-[var(--bg-secondary)]/60 text-xs flex-shrink-0 relative z-20">
          {TOOLBAR_GROUPS.map((group, gIdx) => (
            <div key={gIdx} className="flex items-center gap-0.5 pr-1.5 mr-1.5 border-r last:border-r-0 border-[var(--border-color)]">
              {group.map((btn) => (
                <div key={btn.cmd + (btn.val || "")} className="relative group/tip flex items-center justify-center">
                  <button
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => execCmd(btn.cmd, btn.val)}
                    className="p-1.5 hover:bg-[var(--bg-tertiary)] active:bg-accent/15 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
                    title={btn.title}
                    aria-label={btn.title}
                  >
                    <btn.icon size={13} />
                  </button>
                  {/* Styled Tooltip - Positioned downwards over the editor area to prevent clipping */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 hidden group-hover/tip:flex items-center pointer-events-none z-50 animate-fadeIn">
                    <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] text-2xs font-medium px-2 py-1 whitespace-nowrap shadow-xl">
                      {btn.title}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="relative group/tip flex items-center justify-center">
            <button
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleInsertLink}
              className="p-1.5 hover:bg-[var(--bg-tertiary)] active:bg-accent/15 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex-shrink-0"
              title="Insertar enlace (URL)"
              aria-label="Insertar enlace web"
            >
              <Link2 size={13} />
            </button>
            {/* Styled Tooltip - Positioned downwards */}
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 hidden group-hover/tip:flex items-center pointer-events-none z-50 animate-fadeIn">
              <div className="bg-[var(--bg-tertiary)] border border-[var(--border-color)] text-[var(--text-primary)] text-2xs font-medium px-2 py-1 whitespace-nowrap shadow-xl">
                Insertar enlace (URL)
              </div>
            </div>
          </div>
        </div>

        {/* Editor Body */}
        <div
          className={`flex-1 relative overflow-y-auto cursor-text pb-[max(0.5rem,env(safe-area-inset-bottom))] ${
            isDragging ? "bg-accent/5 ring-2 ring-accent ring-inset" : ""
          }`}
          onClick={(e) => {
            if (e.target === e.currentTarget && editorRef.current) {
              editorRef.current.focus();
            }
          }}
        >
          {isEditorEmpty && !replyTo && (
            <div className="absolute top-4 left-4 sm:left-5 text-sm text-[var(--text-muted)] pointer-events-none select-none opacity-40 font-sans">
              Escribe tu mensaje aquí...
            </div>
          )}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={() => {
              userTouchedRef.current = true;
              checkEditorEmpty();
            }}
            className="compose-editor w-full min-h-full px-4 sm:px-5 py-4 text-sm text-[var(--text-primary)] outline-none font-sans leading-relaxed"
          />
        </div>

        {/* Link Dialog */}
        {showLinkDialog && (
          <div className="px-4 sm:px-5 py-3 border-t border-[var(--border-color)] bg-[var(--bg-secondary)] animate-fadeIn flex-shrink-0">
            <div className="flex items-center gap-2">
              <Link2 size={15} className="text-accent flex-shrink-0" />
              <input
                type="text"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") confirmInsertLink();
                  if (e.key === "Escape") { setShowLinkDialog(false); setLinkUrl(""); }
                }}
                placeholder="https://ejemplo.com"
                autoFocus
                className="flex-1 px-3 py-1.5 text-xs bg-[var(--bg-primary)] border border-[var(--border-color)] text-[var(--text-primary)] outline-none focus:border-accent"
              />
              <button
                onClick={confirmInsertLink}
                disabled={!linkUrl.trim()}
                className="px-3 py-1.5 text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50"
              >
                Insertar
              </button>
              <button
                onClick={() => { setShowLinkDialog(false); setLinkUrl(""); }}
                className="px-3 py-1.5 text-xs border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Attachments List */}
        {attachments.length > 0 && (
          <div className="px-4 sm:px-5 py-2.5 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]/40 flex-shrink-0">
            <div className="flex flex-wrap gap-2">
              {attachments.map((file, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border-color)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)] shadow-2xs"
                >
                  <Paperclip size={12} className="text-[var(--text-muted)] flex-shrink-0" />
                  <span className="truncate max-w-[140px] sm:max-w-[160px] font-medium">{file.name}</span>
                  <span className="text-2xs text-[var(--text-muted)] font-mono">
                    {formatBytes(file.size)}
                  </span>
                  <button
                    onClick={() => removeAttachment(i)}
                    className="p-0.5 hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-500 transition-colors ml-1"
                    title="Quitar archivo"
                    aria-label="Quitar archivo adjunto"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setAttachments([])}
                className="flex items-center gap-1 px-2.5 py-1 text-2xs text-[var(--text-muted)] hover:text-red-500 transition-colors"
              >
                <Trash2 size={11} />
                Quitar todos
              </button>
            </div>
          </div>
        )}

        {/* Error message */}
        {sendError && (
          <div className="px-4 sm:px-5 py-2 text-xs text-red-500 bg-red-500/10 border-t border-red-500/20 font-medium flex-shrink-0">
            {sendError}
          </div>
        )}

        {/* Footer controls */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 sm:px-5 py-3 border-t border-[var(--border-color)] bg-[var(--bg-secondary)] flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleSend}
              disabled={isSending || !to.trim() || !!toError}
              className="flex items-center gap-2 px-5 py-2 text-xs font-semibold bg-accent text-white hover:bg-accent-hover transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
              title="Enviar mensaje (Ctrl+Enter)"
            >
              <Send size={14} className={isSending ? "animate-pulse" : ""} />
              <span>{isSending ? "Enviando..." : "Enviar"}</span>
            </button>

            <button
              type="button"
              onClick={() => handleSaveDraft(false)}
              disabled={isSavingDraft}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors shadow-2xs"
              title="Guardar borrador (Ctrl+S)"
            >
              <Save size={14} />
              <span>{isSavingDraft ? "Guardando..." : "Guardar borrador"}</span>
            </button>

            <label className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)] transition-colors cursor-pointer shadow-2xs">
              <Paperclip size={14} />
              <span>Adjuntar</span>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleAttachment}
                className="hidden"
              />
            </label>

            {attachments.length > 0 && (
              <span className="text-2xs text-[var(--text-muted)] font-mono">
                {attachments.length} archivo{attachments.length > 1 ? "s" : ""} (
                {formatBytes(attachments.reduce((s, f) => s + f.size, 0))})
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-2xs text-[var(--text-muted)] mr-2 hidden md:inline">
              Ctrl+Enter para enviar · Ctrl+S para guardar
            </span>
            <button
              type="button"
              onClick={handleDiscard}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all"
              title="Descartar mensaje y cancelar"
            >
              <Trash2 size={14} />
              <span>Descartar</span>
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}